import { observer } from 'mobx-react';
import React, { useCallback } from 'react';
import ItemExclusion from '../../components/ItemExclusion/ItemExclusion';
import ItemModifier from '../../components/ItemModifier/ItemModifier';
import itemsStore from '../../store/itemsStore';
import integrationsStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { IProductV4Exclusion, IProductV4Modifier } from 'src/types/product';
import { booleanNumberReverse, isDisabledByPos } from 'src/utils/helpers';
import { Checkbox } from 'src/view/components/Controls/Checkbox/Checkbox';
import IntegrationManager from 'src/view/components/IntegrationManager/IntegrationManager';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { MDF_AND_EXCL_MODAL_EXCLUSION, MDF_AND_EXCL_MODAL_MODIFIER } from 'src/view/containers/Modals/ModifierAndExclusionModal/constants';
import './style.scss';
import IntegrationTooltip from '~/view/components/IntegrationTooltip/IntegrationTooltip';
import Switcher from '~/view/components/Controls/Switcher/Switcer';
import integrationStore from '~/mobx/integrationStore';

const ItemModifiersAndExclusions: React.FC = observer((): JSX.Element => {
  const openModifierAndExclusionModal = () => {
    if (!itemsStore.item) return;

    modalStore.openModifierAndExclusionModal({
      selectedModifiers: itemsStore.item.modifiers,
      selectedExclusions: itemsStore.item.exclusions,
      onSave: addModifierOrExclusion,
      modifiersChoice: MDF_AND_EXCL_MODAL_MODIFIER.SINGLE,
      exclusionsChoice: MDF_AND_EXCL_MODAL_EXCLUSION.SINGLE,
      selectOnlyOneItemAvailable: true,
      disabledModifierWithoutOption: true,
    });
  };

  const addModifierOrExclusion = (modifiers: IProductV4Modifier[], exclusions: IProductV4Exclusion[]) => {
    if (modifiers?.length) {
      itemsStore.changeModifiers(modifiers[0]);
    }
    if (exclusions?.length) {
      itemsStore.changeExclusions(exclusions[0]);
    }
  };

  const changeAllPlaces = useCallback(() => {
    itemsStore.changeItemHandler({ apply_modifier_price_to_all: booleanNumberReverse(itemsStore.item?.apply_modifier_price_to_all) });
  }, [itemsStore.item?.apply_modifier_price_to_all]);

  const applyItemOptionStatusToAll = useCallback(() => {
    integrationsStore.integrationNotification();
    itemsStore.changeItemHandler({ apply_item_option_status_to_all: booleanNumberReverse(itemsStore.item?.apply_item_option_status_to_all) });
  }, [itemsStore.item?.apply_item_option_status_to_all]);

  const { itemOptionBranchAvailability, itemOptionBranchPrice } = integrationStore.posRestrictions || {};

  return (
    <div className="d-flex flex-column mt-6">
      <Text type={TEXT.SECTION_HEADER} text="modifiers_and_exclusions" />
      <div className="item-modifiers-exclusions__wrapper mt-4 mr-4">
        <IntegrationManager
          setting={integrationsStore.posRestrictions?.addModifierToItem && integrationsStore.posRestrictions.addExclusionToItem}
          withTooltip
        >
          <button onClick={openModifierAndExclusionModal} className="item-modifiers-exclusions__btn-add">
            {localeStore.t('add_modifiers_or_exclusions')}
          </button>
        </IntegrationManager>
        {itemsStore.item?.modifiers.length ? (
          <div className="d-flex flex-column mt-4">
            <Text type={TEXT.SECTION_HEADER} text="modifiers" className="color-dark" />
            <ul className="my-4">
              {itemsStore.item?.modifiers.map((modifier) => (
                <ItemModifier key={modifier.id} modifier={modifier} />
              ))}
            </ul>
            {!itemsStore.item.is_draft ? (
              <div className="d-flex flex-column mt-2">
                <IntegrationManager setting={itemOptionBranchPrice} wrapperClassName="d-flex flex-row align-items-center mr-8">
                  <IntegrationTooltip isVisible={isDisabledByPos(itemOptionBranchPrice)}>
                    <Checkbox
                      value={Boolean(itemsStore.item?.apply_modifier_price_to_all)}
                      name="apply_modifier_price_to_all"
                      label="apply_modifier_price_to_all"
                      disabled={isDisabledByPos(itemOptionBranchPrice)}
                      onClick={changeAllPlaces}
                    />
                  </IntegrationTooltip>
                </IntegrationManager>
              </div>
            ) : null}
            {!itemsStore.item.is_draft ? (
              <div className="d-flex flex-column mt-2">
                <IntegrationManager setting={itemOptionBranchAvailability} wrapperClassName="d-flex flex-row align-items-center mr-8">
                  <IntegrationTooltip isVisible={isDisabledByPos(itemOptionBranchAvailability)}>
                    <Checkbox
                      value={Boolean(itemsStore.item?.apply_item_option_status_to_all)}
                      name="checkbox_apply_item_modifier_option_availability_to_all"
                      label="checkbox_apply_item_modifier_option_availability_to_all"
                      disabled={isDisabledByPos(itemOptionBranchAvailability)}
                      onClick={applyItemOptionStatusToAll}
                    />
                  </IntegrationTooltip>
                </IntegrationManager>
              </div>
            ) : null}
          </div>
        ) : null}
        {itemsStore.item?.exclusions.length ? (
          <div className="d-flex flex-column mt-4">
            <Text type={TEXT.SECTION_HEADER} text="exclusions" className="color-dark" />
            <ul className="item-modifiers-exclusions__exclusions-wrapper mt-4">
              {itemsStore.item?.exclusions.map((exclusion) => (
                <ItemExclusion key={exclusion.id} exclusion={exclusion} />
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default ItemModifiersAndExclusions;
